// @ts-nocheck
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Loader2, PartyPopper, AlertCircle } from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

function formatDate(d?: string | null) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
}

function getInitials(name?: string | null) {
  if (!name) return 'T';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || 'T';
}

export default function TeamBillingPublicPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const qc = useQueryClient();
  const [isAuthed, setIsAuthed] = useState<boolean>(false);
  const [generating, setGenerating] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setIsAuthed(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => setIsAuthed(!!session));
    return () => sub.subscription.unsubscribe();
  }, []);

  const teamQuery = useQuery({
    queryKey: ['public-team', teamId],
    enabled: !!teamId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('teams')
        .select('id, canonical_name, display_name, stable_slug, age_group, gender, home_state, home_city, logo_url, primary_color')
        .eq('id', teamId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const regsQuery = useQuery({
    queryKey: ['public-team-regs', teamId],
    enabled: !!teamId && !!teamQuery.data,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_team_registrations_summary')
        .select('*')
        .eq('team_id', teamId)
        .gt('balance_due', 0)
        .not('registration_status', 'in', '("dropped","refunded")')
        .order('event_start_date', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const regIds = (regsQuery.data ?? []).map((r: any) => r.event_registration_id).filter(Boolean);

  const linksQuery = useQuery({
    queryKey: ['public-team-links', teamId, regIds.join(',')],
    enabled: regIds.length > 0,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('event_registration_payment_links')
        .select('event_registration_id, stripe_payment_link_url, status, amount')
        .in('event_registration_id', regIds)
        .eq('status', 'active');
      if (error) throw error;
      return data ?? [];
    },
  });

  const linksByReg: Record<string, any> = {};
  (linksQuery.data ?? []).forEach((l: any) => {
    linksByReg[l.event_registration_id] = l;
  });

  const handleGenerate = async (registrationId: string) => {
    setGenerating(registrationId);
    try {
      const { data, error } = await invokeEdgeFunction('rle-create-team-billing-session', {
        body: { event_registration_id: registrationId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error.message || 'Failed to create payment link');
      await qc.invalidateQueries({ queryKey: ['public-team-links', teamId] });
      toast.success('Payment link generated');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to generate payment link');
    } finally {
      setGenerating(null);
    }
  };

  const loading = teamQuery.isLoading || (teamQuery.data && regsQuery.isLoading);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!teamQuery.data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-[600px]">
          <Alert variant="destructive" className="border-destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Team not found.</strong> This payment link may be invalid or the team has been removed.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const team = teamQuery.data;
  const regs = regsQuery.data ?? [];
  const teamName = team.canonical_name || team.display_name || 'Team';

  return (
    <div className="min-h-screen bg-background px-4 py-12 flex flex-col items-center">
      {/* Wordmark */}
      <div className="text-xs font-semibold tracking-wider text-muted-foreground mb-8">
        RLEVENTS
      </div>

      <div className="w-full max-w-[600px] space-y-6">
        {/* Team header */}
        <div className="flex flex-col items-center text-center space-y-3">
          {team.logo_url ? (
            <img
              src={team.logo_url}
              alt={teamName}
              className="h-[72px] w-[72px] rounded-full object-cover border"
            />
          ) : (
            <div
              className="h-[72px] w-[72px] rounded-full flex items-center justify-center text-white font-bold text-xl"
              style={{ backgroundColor: team.primary_color || 'hsl(var(--primary))' }}
            >
              {getInitials(teamName)}
            </div>
          )}
          <h1 className="text-[22px] font-medium leading-tight">{teamName}</h1>
          <p className="text-sm text-muted-foreground">Outstanding payments for this team</p>
        </div>

        {/* Content */}
        {regs.length === 0 ? (
          <Card>
            <CardContent className="py-12 flex flex-col items-center text-center space-y-3">
              <PartyPopper className="h-10 w-10 text-primary" />
              <div className="text-lg font-semibold">All caught up</div>
              <p className="text-sm text-muted-foreground">
                No outstanding payments for {teamName}
              </p>
              <Link
                to="/"
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 mt-2"
              >
                Back to RLevents
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {regs.map((reg: any) => {
              const link = linksByReg[reg.event_registration_id];
              const balance = Number(reg.balance_due ?? 0);
              return (
                <Card key={reg.event_registration_id}>
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-semibold truncate">
                          {reg.event_name ?? 'Event'}
                        </div>
                        <div className="text-sm text-muted-foreground mt-0.5">
                          {formatDate(reg.event_start_date)}
                          {reg.event_end_date && reg.event_end_date !== reg.event_start_date
                            ? ` – ${formatDate(reg.event_end_date)}`
                            : ''}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-orange-500 dark:text-orange-400">
                          ${balance.toFixed(2)}
                        </div>
                        <div className="text-xs text-muted-foreground">remaining</div>
                      </div>
                    </div>

                    <div className="pt-2 border-t">
                      {link ? (
                        <Button
                          asChild
                          className="w-full"
                          size="lg"
                        >
                          <a
                            href={link.stripe_payment_link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Pay ${balance.toFixed(2)}
                          </a>
                        </Button>
                      ) : isAuthed ? (
                        <Button
                          variant="secondary"
                          className="w-full"
                          size="lg"
                          disabled={generating === reg.event_registration_id}
                          onClick={() => handleGenerate(reg.event_registration_id)}
                        >
                          {generating === reg.event_registration_id ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            'Generate payment link'
                          )}
                        </Button>
                      ) : (
                        <p className="text-xs text-muted-foreground text-center">
                          A team member must generate a payment link. Ask your team owner.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <p className="text-xs text-muted-foreground text-center pt-4 leading-relaxed">
          You're paying for the {teamName} team. Your payment goes directly to the tournament organizer.
          RLevents handles no funds directly.
        </p>
      </div>
    </div>
  );
}
